import React, { useState } from "react";
import { AlertTriangle, Home, RefreshCw, Send, CheckCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { trackError, submitSupportTicket } from "@/lib/utils";

// ----------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
}

function ErrorFallback(props: { errorMessage: string | null }) {
  const { errorMessage } = props;
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  function handleReload(): void {
    window.location.reload();
  }

  function handleGoHome(): void {
    window.location.href = "/";
  }

  async function handleSubmitReport(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!description.trim()) return;
    setIsSubmitting(true);
    try {
      const id = await submitSupportTicket(description, errorMessage);
      setTicketId(id);
    } catch (err) {
      console.error("[ErrorBoundary] Failed to submit ticket:", err);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 px-6 py-12">
      <div className="w-full max-w-2xl rounded-3xl border border-gray-200 bg-white p-8 shadow-xl shadow-slate-200/60 sm:p-10">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-50 text-red-600">
          <AlertTriangle className="h-10 w-10" />
        </div>

        <div className="mt-6 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
            Qualcosa è andato storto
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-gray-500 sm:text-base">
            Si è verificato un errore inatteso. Puoi ricaricare l&apos;app
            oppure tornare alla dashboard per riprendere il lavoro.
          </p>
        </div>

        {errorMessage && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span className="font-medium">Dettaglio:</span> {errorMessage}
          </div>
        )}

        {/* Report Support Ticket Form */}
        <div className="mt-6 border-t border-gray-100 pt-6">
          {!showForm && !ticketId && (
            <button
              onClick={() => setShowForm(true)}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 underline cursor-pointer"
            >
              Segnala questo problema al supporto tecnico B2B
            </button>
          )}

          {showForm && !ticketId && (
            <form onSubmit={handleSubmitReport} className="space-y-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Descrivi cosa stavi facendo
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="es: Stavo creando un nuovo ordine ed ho cliccato su aggiungi prodotto..."
                className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-[#0D6EFD] focus:outline-none focus:ring-1 focus:ring-[#0D6EFD] bg-white text-gray-900"
                required
              />
              <div className="flex gap-3 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowForm(false)}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !description.trim()}
                  className="bg-[#0D6EFD] text-white hover:bg-blue-700"
                >
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  {isSubmitting ? "Invio..." : "Invia segnalazione"}
                </Button>
              </div>
            </form>
          )}

          {ticketId && (
            <div className="rounded-xl bg-green-50 border border-green-100 p-4 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-green-800">
                  Segnalazione inviata con successo!
                </div>
                <div className="text-xs text-green-700 mt-1">
                  Il team tecnico è stato notificato. ID ticket: <span className="font-mono font-semibold">{ticketId}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center border-t border-gray-100 pt-6">
          <Button
            className="bg-[#0D6EFD] text-white hover:bg-blue-700"
            onClick={handleReload}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Ricarica applicazione
          </Button>
          <Button variant="outline" onClick={handleGoHome}>
            <Home className="mr-2 h-4 w-4" />
            Torna alla dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      errorMessage: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error.message || "Errore sconosciuto",
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("[ErrorBoundary] Unhandled application error:", {
      error,
      componentStack: errorInfo.componentStack,
    });
    void trackError(error, { componentStack: errorInfo.componentStack });
  }

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback errorMessage={this.state.errorMessage} />;
    }

    return this.props.children;
  }
}
